// src/pages/JourneyMap/index.jsx
// Orquestrador da Jornada Edy — 5 passos com estado real no MongoDB
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import TopPageHeader from '../../ui/PageHeader';
import JourneyStepper from './components/JourneyStepper';
import EdyAssistant   from './components/EdyAssistant';
import Step1Profile   from './phases/Step1Profile';
import Step2Creation  from './phases/Step2Creation';
import Step3Refine    from './phases/Step3Refine';
import Step4Boost     from './phases/Step4Boost';
import Step5Sales     from './phases/Step5Sales';
import apiClient      from '../../lib/apiClient';
import './journey.css';

/* Mapeia status de backend → índice de passo (0-based) */
const STATUS_TO_STEP = {
  perfil:   0,
  criacao:  1,
  refino:   2,
  turbinar: 3,
  vendas:   4,
};

/* Painel direito com o robô Edy */
function RobotPanel({ mood = 'default' }) {
  return (
    <div className={`jm-right mood-${mood}`}>
      <div className="jm-edy-img-wrap">
        <img src="/robot.png" alt="" className="jm-edy-img" />
      </div>
    </div>
  );
}

export default function JourneyMapPage() {
  const [params]       = useSearchParams();
  const resumeId       = params.get('c');

  const [campaign, setCampaign] = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [toast,    setToast]    = useState('');
  const [edyMood,  setEdyMood]  = useState('default');

  // Escuta evento de mood disparado pelo EdyAssistant dentro de cada step
  useEffect(() => {
    const handler = (e) => setEdyMood(e.detail || 'default');
    window.addEventListener('edy-mood', handler);
    return () => window.removeEventListener('edy-mood', handler);
  }, []);

  // ---------- bootstrap: resume ou cria nova jornada ----------
  const startJourney = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await apiClient.request('/mp/start', { method: 'POST', body: JSON.stringify({}) });
      const d = await r.json();
      if (d.success && d.campaign) {
        setCampaign(d.campaign);
        // Atualiza URL sem reload
        const url = new URL(window.location.href);
        url.searchParams.set('c', d.campaign.id);
        window.history.replaceState(null, '', url.toString());
      } else {
        setError(d.message || 'Erro ao iniciar jornada.');
      }
    } catch {
      setError('Erro de conexão ao iniciar jornada.');
    } finally {
      setLoading(false);
    }
  }, []);

  const resumeJourney = useCallback(async (id) => {
    setLoading(true);
    setError('');
    try {
      const r = await apiClient.request(`/mp/campaign/${id}`);
      const d = await r.json();
      if (d.success && d.campaign) {
        setCampaign(d.campaign);
      } else {
        // ID inválido/expirado — inicia nova
        await startJourney();
      }
    } catch {
      await startJourney();
    } finally {
      setLoading(false);
    }
  }, [startJourney]);

  useEffect(() => {
    if (resumeId) {
      resumeJourney(resumeId);
    } else {
      startJourney();
    }
  }, []); // eslint-disable-line

  // ---------- callback chamado por cada step ao avançar ----------
  const handleAdvance = (updatedCampaign) => {
    setCampaign(updatedCampaign);
    const statusLabel = {
      criacao:  'Perfil salvo! Agora vamos criar seu criativo',
      refino:   'Criativo aprovado! Hora de refinar os detalhes',
      turbinar: 'Refinamentos salvos! Escolha seu plano',
      vendas:   'Pagamento confirmado! Seus anúncios estão no ar!',
    };
    const msg = statusLabel[updatedCampaign?.status] || 'Passo concluído!';
    setToast(msg);
    setTimeout(() => setToast(''), 3500);
  };

  // ---------- "Repetir Sucesso" no Step5 ----------
  const handleRepeat = async () => {
    setCampaign(null);
    setLoading(true);
    // Remove query param
    const url = new URL(window.location.href);
    url.searchParams.delete('c');
    window.history.replaceState(null, '', url.toString());
    await startJourney();
  };

  // ---------- render auxiliares ----------
  if (loading) {
    return (
      <div className="jm-page">
        <TopPageHeader />
        <div className="jm-layout">
          <div className="jm-left">
            <div className="jm-container">
              <EdyAssistant message="Preparando sua jornada personalizada..." mood="think" loading />
            </div>
          </div>
          <RobotPanel mood="think" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="jm-page">
        <TopPageHeader />
        <div className="jm-layout">
          <div className="jm-left">
            <div className="jm-container">
              <EdyAssistant message="Ops! Tive um problema para iniciar sua jornada." mood="think" />
              <p style={{ color: 'var(--edy-red)', margin: '16px 0' }}>{error}</p>
              <button className="jm-btn-cta" onClick={startJourney}>Tentar novamente</button>
            </div>
          </div>
          <RobotPanel mood="think" />
        </div>
      </div>
    );
  }

  const currentStatus = campaign?.status || 'perfil';
  const stepIdx       = STATUS_TO_STEP[currentStatus] ?? 0;

  return (
    <div className="jm-page">
      <TopPageHeader />

      {/* Stepper — largura total */}
      <JourneyStepper currentStatus={currentStatus} />

      {/* Toast de progresso */}
      {toast && <div className="jm-toast">{toast}</div>}

      {/* Layout 2 colunas */}
      <div className="jm-layout">
        {/* ESQUERDA: conteúdo do passo */}
        <div className="jm-left">
          {stepIdx === 0 && (
            <Step1Profile
              campaignId={campaign?.id}
              onAdvance={handleAdvance}
            />
          )}
          {stepIdx === 1 && (
            <Step2Creation
              campaignId={campaign?.id}
              profileData={campaign?.profile_data}
              onAdvance={handleAdvance}
            />
          )}
          {stepIdx === 2 && (
            <Step3Refine
              campaignId={campaign?.id}
              onAdvance={handleAdvance}
            />
          )}
          {stepIdx === 3 && (
            <Step4Boost
              campaignId={campaign?.id}
              onAdvance={handleAdvance}
            />
          )}
          {stepIdx === 4 && (
            <Step5Sales
              campaignId={campaign?.id}
              onRepeat={handleRepeat}
            />
          )}
        </div>

        {/* DIREITA: robô Edy */}
        <RobotPanel mood={edyMood} />
      </div>
    </div>
  );
}
